import React, { useEffect, useState, useRef } from "react";
import {
  MessageCircle,
  Phone,
  ArrowRight,
  ArrowDown,
  Gem,
  Hammer,
  Coins,
  Sparkles,
  ExternalLink,
  Plus,
} from "lucide-react";

// ──────────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────────
const WHATSAPP_URL = "https://wa.me/447718248627";
const PHONE_DISPLAY = "+44 7718 248 627";
const PHONE_HREF = "tel:+447718248627";

const NAV_ITEMS = [
  { label: "About", href: "#about" },
  { label: "Sell", href: "#whats-worth" },
  { label: "Buy", href: "#sourcing" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Valuations", href: "#valuations" },
  { label: "Markets", href: "#markets" },
];

// Curated luxury imagery
const IMG_HERO =
  "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=1800&q=80";
const IMG_WORTH =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_gold-main-site/artifacts/fjrmb6lx_DSC_0318-2.webp";
const IMG_SOURCING =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_gold-main-site/artifacts/xovqmdma_IMG_0074.webp";
const IMG_VALUATION =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_gold-main-site/artifacts/xovqmdma_IMG_0074.webp";
const IMG_ABOUT =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_gold-main-site/artifacts/mwofewjf_IMG_3328.webp";
const IMG_MARKET =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_gold-main-site/artifacts/4qxb3zz8_IMG_0073.webp";

// ──────────────────────────────────────────────────────────────────
// Reveal-on-scroll hook
// ──────────────────────────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

// ──────────────────────────────────────────────────────────────────
// Diamond divider (◆ ◆ ◆)
// ──────────────────────────────────────────────────────────────────
const DiamondDivider = ({ className = "" }) => (
  <div className={`diamond-divider ${className}`} data-testid="diamond-divider">
    <span>◆</span>
    <span>◆</span>
    <span>◆</span>
  </div>
);

// ──────────────────────────────────────────────────────────────────
// Accordion (mobile-only collapsible list item)
// ──────────────────────────────────────────────────────────────────
const AccordionItem = ({
  testid,
  index = 0,
  leadingLabel,
  leadingIcon,
  title,
  trailing,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const contentRef = useRef(null);

  return (
    <div
      data-testid={testid}
      className={`border-b border-[rgba(241,200,127,0.18)] ${
        index === 0 ? "border-t" : ""
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
      >
        <span className="flex items-center gap-4 min-w-0 flex-1">
          {leadingLabel && (
            <span className="font-display text-gold-grad text-[26px] leading-none shrink-0 opacity-90">
              {leadingLabel}
            </span>
          )}
          {leadingIcon && (
            <span className="w-9 h-9 flex items-center justify-center border border-[var(--cg-gold)] text-[var(--cg-gold)] shrink-0">
              {leadingIcon}
            </span>
          )}
          <span className="font-display text-[18px] leading-tight text-[var(--cg-cream)] group-hover:text-[var(--cg-gold)] transition-colors duration-300 truncate">
            {title}
          </span>
        </span>
        <span className="flex items-center gap-3 shrink-0">
          {trailing && !open && (
            <span className="hidden sm:inline">{trailing}</span>
          )}
          <span
            className={`w-8 h-8 rounded-full border border-[rgba(241,200,127,0.35)] flex items-center justify-center text-[var(--cg-gold)] transition-all duration-500 ${
              open ? "rotate-45 bg-[rgba(241,200,127,0.08)]" : ""
            }`}
          >
            <Plus size={14} strokeWidth={1.6} />
          </span>
        </span>
      </button>

      <div
        ref={contentRef}
        className="grid transition-[grid-template-rows] duration-500 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="pb-6 pt-1 pr-1">{children}</div>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────
// Navigation
// ──────────────────────────────────────────────────────────────────
const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleClick = (item) => {
    setOpen(false);
    if (item.external) {
      window.open(item.href, "_blank", "noopener,noreferrer");
    } else {
      // Delay slightly to let overlay close
      setTimeout(() => {
        const el = document.querySelector(item.href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled || open
            ? "nav-blur border-b border-[rgba(241,200,127,0.12)]"
            : ""
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-6">
          <button
            data-testid="nav-logo"
            onClick={() => {
              setOpen(false);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3 group shrink-0"
          >
            <span className="text-[var(--cg-gold)] text-xl tracking-[0.05em]">◆</span>
            <span className="font-display text-[18px] md:text-[22px] tracking-[0.16em] text-[var(--cg-cream)] group-hover:text-[var(--cg-gold)] transition-colors duration-500 whitespace-nowrap">
              COLLECTIVE<span className="text-gold-grad">GOLD</span>
            </span>
          </button>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-3 text-[var(--cg-cream)] hover:text-[var(--cg-gold)] transition-colors duration-300 group"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <span className="hidden sm:inline text-[11px] tracking-[0.32em] uppercase">
              {open ? "Close" : "Menu"}
            </span>
            <span className="relative w-8 h-6 flex flex-col justify-center items-end gap-[6px]">
              <span
                className={`block h-px bg-current transition-all duration-500 ${
                  open ? "w-8 rotate-45 translate-y-[7px]" : "w-8"
                }`}
              />
              <span
                className={`block h-px bg-current transition-all duration-500 ${
                  open ? "w-8 -rotate-45 -translate-y-[3px]" : "w-5"
                }`}
              />
            </span>
          </button>
        </div>
      </header>

      {/* Full-screen overlay menu */}
      <div
        data-testid="overlay-menu"
        className={`fixed inset-0 z-40 transition-all duration-700 ${
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        style={{
          background:
            "radial-gradient(ellipse at top, #0a0a0a 0%, #000 60%, #000 100%)",
        }}
      >
        <div className="h-full w-full flex flex-col justify-center pt-24 pb-16 px-6 md:px-12 overflow-y-auto">
          <div className="max-w-[900px] mx-auto w-full">
            <div
              className={`eyebrow mb-10 transition-all duration-700 ${
                open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: open ? "0.15s" : "0s" }}
            >
              ◆ &nbsp; Navigation
            </div>
            <nav className="flex flex-col gap-2 md:gap-4 mb-12">
              {NAV_ITEMS.map((item, i) => (
                <button
                  key={item.label}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => handleClick(item)}
                  className={`group text-left flex items-center justify-between py-4 md:py-6 border-b border-[rgba(241,200,127,0.14)] hover:border-[var(--cg-gold)] transition-all duration-500 ${
                    open
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-6"
                  }`}
                  style={{
                    transitionDelay: open ? `${0.2 + i * 0.08}s` : "0s",
                  }}
                >
                  <span className="flex items-baseline gap-4 md:gap-6">
                    <span className="eyebrow !text-[10px] text-[var(--cg-gold)]/60 w-8">
                      0{i + 1}
                    </span>
                    <span className="font-display text-[32px] md:text-[52px] leading-[1.05] text-[var(--cg-cream)] group-hover:text-[var(--cg-gold)] transition-colors duration-500">
                      {item.label}
                    </span>
                  </span>
                  <span className="flex items-center gap-3 text-[var(--cg-gold)] opacity-40 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-500">
                    {item.external ? (
                      <ExternalLink size={20} strokeWidth={1.4} />
                    ) : (
                      <ArrowRight size={22} strokeWidth={1.4} />
                    )}
                  </span>
                </button>
              ))}
            </nav>

            <div
              className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 transition-all duration-700 ${
                open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{
                transitionDelay: open
                  ? `${0.2 + NAV_ITEMS.length * 0.08}s`
                  : "0s",
              }}
            >
              <a
                data-testid="overlay-cta-whatsapp"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
              >
                <MessageCircle size={15} strokeWidth={1.8} />
                Request Instant Advice
              </a>
              <a
                data-testid="overlay-cta-phone"
                href={PHONE_HREF}
                className="btn-ghost"
              >
                <Phone size={13} />
                {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ──────────────────────────────────────────────────────────────────
// Hero
// ──────────────────────────────────────────────────────────────────
const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      id="home"
      className="relative min-h-[100vh] flex items-end overflow-hidden grain-overlay vignette"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMG_HERO})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-40 w-full">
        <div className="max-w-4xl">
          <div
            className="reveal text-[var(--cg-gold)] text-lg tracking-[0.5em] mb-8"
            style={{ animationDelay: "0.1s" }}
            data-testid="hero-diamond"
          >
            ◆
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display reveal text-[44px] sm:text-[64px] md:text-[88px] lg:text-[104px] leading-[1.05] tracking-[-0.01em] text-[var(--cg-cream)] mb-8"
            style={{ animationDelay: "0.25s" }}
          >
            <span className="hero-line">Your Route to</span>
            <span className="hero-line font-italic-serif text-gold-grad">
              Buying &amp; Selling
            </span>
            <span className="hero-line">Jewellery, Watches</span>
            <span className="hero-line">&amp; Antiques</span>
          </h1>

          <p
            className="reveal font-italic-serif text-[16px] sm:text-[18px] md:text-[20px] lg:text-[22px] text-[var(--cg-cream)]/85 mb-10 max-w-[42rem] lg:whitespace-nowrap lg:max-w-none"
            style={{ animationDelay: "0.45s" }}
          >
            Direct offers to and from trusted buyers — a discreet, direct route to buying or selling.
          </p>

          <div
            className="reveal flex flex-wrap items-center gap-4"
            style={{ animationDelay: "0.6s" }}
          >
            <a
              data-testid="hero-cta-whatsapp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
            >
              <MessageCircle size={15} strokeWidth={1.8} />
              Request Instant Advice
            </a>
            <button
              data-testid="hero-cta-how-it-works"
              onClick={() =>
                document
                  .querySelector("#how-it-works")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="btn-ghost"
            >
              How It Works
              <ArrowDown size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[var(--cg-gold)] opacity-60 reveal" style={{ animationDelay: "0.9s" }}>
        <span className="text-[9px] tracking-[0.5em] uppercase">Scroll</span>
        <span className="h-10 w-px bg-gradient-to-b from-[var(--cg-gold)] to-transparent animate-pulse" />
      </div>
    </section>
  );
};

// ──────────────────────────────────────────────────────────────────
// What's It Worth?
// ──────────────────────────────────────────────────────────────────
const WhatsItWorth = () => (
  <section
    data-testid="whats-worth-section"
    id="whats-worth"
    className="relative py-28 md:py-40 px-6 md:px-12"
  >
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      {/* Image */}
      <div className="lg:col-span-5 reveal">
        <div className="frame-gold relative aspect-[4/5] overflow-hidden">
          <img
            src={IMG_WORTH}
            alt="Fine jewellery being examined"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent" />
        </div>
      </div>

      {/* Copy */}
      <div className="lg:col-span-7">
        <div className="eyebrow reveal mb-6">◆ &nbsp; Selling</div>
        <h2
          data-testid="whats-worth-headline"
          className="reveal font-display text-[42px] md:text-[64px] leading-[1.02] tracking-[-0.01em] text-[var(--cg-cream)] mb-8"
        >
          What's It <span className="font-italic-serif text-gold-grad">Worth?</span>
        </h2>
        <div className="reveal space-y-6 text-[16px] md:text-[17px] leading-[1.85] text-[var(--cg-cream)]/80 max-w-2xl">
          <p>
            For those seriously considering a sale, CollectiveGold helps clients sort
            through, value, and sell pre-loved jewellery, watches, and antique
            pieces — securing{" "}
            <span className="text-[var(--cg-gold)] italic">direct purchase offers</span>{" "}
            from established, vetted buyers on your behalf.
          </p>
          <p>
            Whether reviewing a single piece or a wider collection, Isabelle offers
            a discreet, personal approach — including free home visits where
            appropriate — while working through a trusted network of specialists,
            private collectors, and leading auction houses to obtain genuine offers
            and expert valuations for you to consider.
          </p>
          <p>
            Valuations for insurance, probate and auction purposes are available as
            a supporting service.
          </p>
          <p className="font-italic-serif text-[22px] md:text-[24px] text-[var(--cg-gold)] not-italic">
            <span className="font-italic-serif italic">
              A clear, considered route to sale — on your terms.
            </span>
          </p>
        </div>
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────
// Looking to Buy? / Sourcing
// ──────────────────────────────────────────────────────────────────
const Sourcing = () => (
  <section
    data-testid="sourcing-section"
    id="sourcing"
    className="relative py-28 md:py-40 px-6 md:px-12 bg-[var(--cg-ink)]"
  >
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      {/* Copy first on desktop, image second — mirrors sell layout for balance */}
      <div className="lg:col-span-7 order-2 lg:order-1">
        <div className="eyebrow reveal mb-6">◆ &nbsp; Buying</div>
        <h2
          data-testid="sourcing-headline"
          className="reveal font-display text-[42px] md:text-[64px] leading-[1.02] tracking-[-0.01em] text-[var(--cg-cream)] mb-8"
        >
          Looking to{" "}
          <span className="font-italic-serif text-gold-grad">Buy?</span>
        </h2>
        <div className="reveal space-y-6 text-[16px] md:text-[17px] leading-[1.85] text-[var(--cg-cream)]/80 max-w-2xl">
          <p>
            For clients searching for something specific — a meaningful heirloom,
            the perfect antique, a signed find, or simply your next dream piece
            — CollectiveGold quietly sources on your behalf through the same
            trusted network used to sell.
          </p>
          <p>
            Isabelle draws on relationships with private dealers, specialist
            jewellers, unsold auction lots and collectors' inventories to{" "}
            <span className="text-[var(--cg-gold)] italic">
              locate your piece
            </span>{" "}
            — often at direct, value-driven prices.
          </p>
          <p>
            Whether replacing a lost heirloom, acquiring for a wider collection,
            arranging{" "}
            <span className="text-[var(--cg-gold)]">bespoke commissions</span>{" "}
            or sourcing loose diamonds &amp; coloured stones, every search is
            handled discreetly and only pieces that meet your brief are
            presented.
          </p>
          <p className="font-italic-serif text-[22px] md:text-[24px] text-[var(--cg-gold)]">
            <span className="italic">
              A patient, personal search — on your terms.
            </span>
          </p>

          {/* Search brief bullets */}
          <ul className="grid sm:grid-cols-2 gap-4 pt-4">
            {[
              "Antique & meaningful pieces",
              "Fine watches & timepieces",
              "Signed & designer jewellery",
              "Bespoke commissions",
              "Loose diamonds & coloured stones",
              "Estate & unsold auction lots",
            ].map((item) => (
              <li
                key={item}
                data-testid={`sourcing-brief-${item.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="flex items-start gap-3 text-[14px] md:text-[15px] text-[var(--cg-cream)]/85"
              >
                <span className="text-[var(--cg-gold)] mt-1 text-[9px]">◆</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="pt-6">
            <a
              data-testid="sourcing-cta-whatsapp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
            >
              <MessageCircle size={15} strokeWidth={1.8} />
              Share Your Brief
            </a>
          </div>
        </div>
      </div>

      {/* Image */}
      <div className="lg:col-span-5 order-1 lg:order-2 reveal">
        <div className="frame-gold relative aspect-[4/5] overflow-hidden">
          <img
            src={IMG_SOURCING}
            alt="Examining a piece with a loupe at an auction desk"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-bl from-black/60 via-transparent to-transparent" />
        </div>
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────
// How It Works
// ──────────────────────────────────────────────────────────────────
const STEPS = [
  {
    n: "01",
    title: "Speak with Isabelle",
    body: "Send images and a few details of your piece if you're considering a sale — or share a reference and brief if you're looking to source something specific. Guidance is also provided in person when sorting through collections or discussing what to buy.",
  },
  {
    n: "02",
    title: "Offers & Options Obtained",
    body: "For sellers, I approach trusted buyers and dealers to secure direct purchase offers. For buyers, I present sourced options from my network — including auction, private and bespoke routes. Valuations for auction, probate and insurance can also be arranged.",
  },
  {
    n: "03",
    title: "Payment — Send or Collect",
    body: "Once you're happy to proceed, payment is arranged via bank transfer. Your piece is then either collected from you (selling) or sent to you (buying) — securely and discreetly.",
  },
];

const HowItWorks = () => (
  <section
    data-testid="how-it-works-section"
    id="how-it-works"
    className="relative py-28 md:py-40 px-6 md:px-12 bg-[var(--cg-ink)]"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="text-center mb-20 md:mb-28">
        <div className="eyebrow reveal mb-6">◆ &nbsp; The Process</div>
        <h2
          data-testid="how-it-works-headline"
          className="reveal font-display text-[44px] md:text-[72px] leading-[1.02] tracking-[-0.01em] text-[var(--cg-cream)] mb-6"
        >
          How <span className="font-italic-serif text-gold-grad">It Works</span>
        </h2>
        <p className="reveal font-italic-serif text-[20px] md:text-[24px] text-[var(--cg-cream)]/65 max-w-2xl mx-auto">
          A discreet three-step journey, refined for clarity and confidence.
        </p>
      </div>

      {/* Desktop: 3-column layout */}
      <div className="hidden md:grid grid-cols-3 gap-6">
        {STEPS.map((s, i) => (
          <div
            key={s.n}
            data-testid={`how-step-${i + 1}`}
            className="reveal relative"
            style={{ animationDelay: `${0.15 * (i + 1)}s` }}
          >
            <div className="px-8 py-10 border-l border-[rgba(241,200,127,0.18)] h-full">
              <div className="font-display text-gold-grad text-[110px] leading-none mb-6 opacity-90">
                {s.n}
              </div>
              <h3 className="font-display text-[28px] text-[var(--cg-cream)] mb-5 tracking-tight">
                {s.title}
              </h3>
              <p className="text-[15px] leading-[1.85] text-[var(--cg-cream)]/70">
                {s.body}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile: accordion */}
      <div className="md:hidden reveal">
        {STEPS.map((s, i) => (
          <AccordionItem
            key={s.n}
            testid={`how-step-${i + 1}`}
            index={i}
            leadingLabel={s.n}
            title={s.title}
            defaultOpen={i === 0}
          >
            <p className="text-[14px] leading-[1.85] text-[var(--cg-cream)]/75">
              {s.body}
            </p>
          </AccordionItem>
        ))}
      </div>

      <div className="mt-16 md:mt-24">
        <DiamondDivider />
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────
// Valuation Types
// ──────────────────────────────────────────────────────────────────
const VALUATIONS = [
  {
    icon: Coins,
    title: "Direct Purchase Offers",
    body: "Offers sourced from trusted jewellers, dealers & buyers considering immediate purchase — prices reflect what they're prepared to pay directly.",
    price: "10% fee on successful agreement",
  },
  {
    icon: Hammer,
    title: "Auction & Probate Valuations",
    body: "What your piece may achieve on the open market, based on demand. Suited for estate planning, probate, or sale via auction houses.",
    price: "£50 or complimentary*",
  },
  {
    icon: Gem,
    title: "Insurance Valuations",
    body: "Determines the cost to replace your item for insurance purposes if lost or stolen — issued with formal documentation.",
    price: "£85",
  },
];

const ValuationTypes = () => (
  <section
    data-testid="valuations-section"
    id="valuations"
    className="relative py-28 md:py-40 px-6 md:px-12 overflow-hidden"
  >
    {/* Background imagery */}
    <div className="absolute inset-0 opacity-[0.08]">
      <img src={IMG_VALUATION} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />

    <div className="relative z-10 max-w-[1400px] mx-auto">
      <div className="text-center mb-20 md:mb-24">
        <div className="eyebrow reveal mb-6">◆ &nbsp; Valuation Types</div>
        <h2
          data-testid="valuations-headline"
          className="reveal font-display text-[44px] md:text-[72px] leading-[1.02] tracking-[-0.01em] text-[var(--cg-cream)] mb-8"
        >
          Understanding{" "}
          <span className="font-italic-serif text-gold-grad">Your Options</span>
        </h2>
        <p className="reveal font-italic-serif text-[19px] md:text-[22px] text-[var(--cg-cream)]/70 max-w-3xl mx-auto leading-relaxed">
          Whether buying, selling or safeguarding a piece, it's important to
          understand which valuation best supports your outcome — I'm here to
          guide you through the options.
        </p>
      </div>

      {/* Desktop: 3-card grid */}
      <div className="hidden md:grid grid-cols-3 gap-8 mb-16">
        {VALUATIONS.map((v, i) => {
          const Icon = v.icon;
          return (
            <div
              key={v.title}
              data-testid={`valuation-card-${i + 1}`}
              className="reveal relative border border-[rgba(241,200,127,0.18)] bg-black/40 p-10 hover:border-[rgba(241,200,127,0.5)] transition-all duration-700 group"
              style={{ animationDelay: `${0.15 * (i + 1)}s` }}
            >
              <div className="w-12 h-12 flex items-center justify-center border border-[var(--cg-gold)] text-[var(--cg-gold)] mb-8 group-hover:bg-[var(--cg-gold)] group-hover:text-black transition-all duration-500">
                <Icon size={20} strokeWidth={1.4} />
              </div>
              <h3 className="font-display text-[24px] text-[var(--cg-cream)] mb-5 tracking-tight leading-tight">
                {v.title}
              </h3>
              <p className="text-[14px] leading-[1.85] text-[var(--cg-cream)]/70 mb-8">
                {v.body}
              </p>
              <div className="pt-6 border-t border-[rgba(241,200,127,0.18)]">
                <div className="eyebrow !text-[10px] mb-1.5">Fee</div>
                <div className="font-display text-gold-grad text-[20px]">
                  {v.price}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: accordion */}
      <div className="md:hidden reveal mb-14">
        {VALUATIONS.map((v, i) => {
          const Icon = v.icon;
          return (
            <AccordionItem
              key={v.title}
              testid={`valuation-card-${i + 1}`}
              index={i}
              leadingIcon={<Icon size={16} strokeWidth={1.4} />}
              title={v.title}
              trailing={
                <span className="font-display text-gold-grad text-[13px]">
                  {v.price}
                </span>
              }
            >
              <p className="text-[14px] leading-[1.85] text-[var(--cg-cream)]/75 mb-4">
                {v.body}
              </p>
              <div className="pt-4 border-t border-[rgba(241,200,127,0.14)]">
                <div className="eyebrow !text-[10px] mb-1">Fee</div>
                <div className="font-display text-gold-grad text-[18px]">
                  {v.price}
                </div>
              </div>
            </AccordionItem>
          );
        })}
      </div>

      <div className="reveal max-w-3xl mx-auto text-center">
        <p className="text-[13px] md:text-[14px] text-[var(--cg-cream)]/60 italic leading-relaxed">
          *Wish to sell at auction? — valuation fees are waived and complete
          consignment support is provided when a sale goes through CollectiveGold.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a
            data-testid="valuations-cta-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold"
          >
            <MessageCircle size={15} />
            Request Instant Advice
          </a>
        </div>
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────
// About Isabelle
// ──────────────────────────────────────────────────────────────────
const About = () => (
  <section
    data-testid="about-section"
    id="about"
    className="relative py-28 md:py-40 px-6 md:px-12 bg-[var(--cg-ink)]"
  >
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      <div className="lg:col-span-6 order-2 lg:order-1">
        <div className="eyebrow reveal mb-6">◆ &nbsp; The Founder</div>
        <h2
          data-testid="about-headline"
          className="reveal font-display text-[44px] md:text-[68px] leading-[1.02] tracking-[-0.01em] text-[var(--cg-cream)] mb-3"
        >
          About <span className="font-italic-serif text-gold-grad">Isabelle</span>
        </h2>
        <p className="reveal font-italic-serif text-[20px] md:text-[22px] text-[var(--cg-cream)]/70 mb-10">
          Founder of CollectiveGold
        </p>

        <div className="reveal space-y-6 text-[15px] md:text-[16px] leading-[1.85] text-[var(--cg-cream)]/80 max-w-xl">
          <p>
            Belle, Founder @CollectiveGold, has built up an immensely valuable
            range of contacts, working for luxury auction houses and renowned fine
            jewellery brands including{" "}
            <span className="text-[var(--cg-gold)]">Bulgari</span> and{" "}
            <span className="text-[var(--cg-gold)]">Tiffany</span>.
          </p>
          <p>
            Her trusted network of specialists, valuers, dealers and industry
            contacts helps secure reliable valuations, leading direct purchase
            offers, strong sales results at auction, and{" "}
            <span className="text-[var(--cg-gold)]">
              privately-sourced acquisitions
            </span>{" "}
            for clients searching for something specific.
          </p>
          <p>
            She has also achieved incredible feedback for her service —
            understanding how to make the valuation, sourcing and sales process
            feel less confusing while helping clients achieve the best possible
            outcomes.
          </p>
          <p className="font-italic-serif text-[20px] md:text-[22px] text-[var(--cg-cream)] italic">
            Simply put, CollectiveGold was created to offer a more personal,
            discreet and hassle-free approach to sourcing, sorting, valuing
            &amp; selling jewellery, watches and antique pieces. Professional
            cleaning &amp; restoration is available as an additional service.
          </p>
        </div>
      </div>

      <div className="lg:col-span-6 order-1 lg:order-2 reveal">
        <div className="frame-gold relative aspect-[4/5] overflow-hidden">
          <img
            src={IMG_ABOUT}
            alt="Isabelle, founder of CollectiveGold"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 right-8">
            <div className="eyebrow mb-2">Founder</div>
            <div className="font-display text-[28px] md:text-[36px] text-[var(--cg-cream)] leading-tight">
              Isabelle{" "}
              <span className="font-italic-serif text-gold-grad">"Belle"</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────
// Markets
// ──────────────────────────────────────────────────────────────────
const Markets = () => (
  <section
    data-testid="markets-section"
    id="markets"
    className="relative py-28 md:py-40 px-6 md:px-12 overflow-hidden"
  >
    <div className="absolute inset-0 opacity-[0.15]">
      <img src={IMG_MARKET} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />

    <div className="relative z-10 max-w-[1100px] mx-auto text-center">
      <div className="eyebrow reveal mb-6">◆ &nbsp; In Person</div>
      <h2
        data-testid="markets-headline"
        className="reveal font-display text-[44px] md:text-[72px] leading-[1.02] tracking-[-0.01em] text-[var(--cg-cream)] mb-8"
      >
        Meet Isabelle at{" "}
        <span className="font-italic-serif text-gold-grad">Selected Markets</span>
      </h2>
      <div className="reveal space-y-5 text-[16px] md:text-[17px] leading-[1.85] text-[var(--cg-cream)]/75 max-w-2xl mx-auto mb-12">
        <p>
          Professional jewellery cleaning, consultations and viewings of sourced
          pieces are available at selected markets and pop-up events.
        </p>
        <p>
          A relaxed way to discuss jewellery, watches, antiques, valuations, or
          pieces you may be considering buying or selling.
        </p>
      </div>

      <div className="reveal market-card inline-block border border-[rgba(241,200,127,0.25)] px-8 md:px-12 py-8 md:py-10 bg-black/30 mb-12">
        <div className="flex items-center gap-3 text-[var(--cg-gold)] mb-3 justify-center">
          <Sparkles size={16} strokeWidth={1.4} />
          <span className="eyebrow !text-[10px]">Next Pop-Up</span>
        </div>
        <div className="font-display text-[22px] md:text-[28px] text-[var(--cg-cream)]">
          Message Isabelle directly for{" "}
          <span className="font-italic-serif text-gold-grad">
            upcoming dates &amp; locations
          </span>
        </div>
      </div>

      <div className="reveal">
        <a
          data-testid="markets-cta-whatsapp"
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold"
        >
          <MessageCircle size={15} />
          Request Instant Advice
        </a>
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────
// Contact
// ──────────────────────────────────────────────────────────────────
const Contact = () => (
  <section
    data-testid="contact-section"
    id="contact"
    className="relative py-28 md:py-40 px-6 md:px-12 bg-[var(--cg-ink)]"
  >
    <div className="max-w-[900px] mx-auto text-center">
      <div className="eyebrow reveal mb-6">◆ &nbsp; Get in Touch</div>
      <h2
        data-testid="contact-headline"
        className="reveal font-display text-[48px] md:text-[80px] leading-[1.02] tracking-[-0.01em] text-[var(--cg-cream)] mb-6"
      >
        Ready to <span className="font-italic-serif text-gold-grad">Get Started?</span>
      </h2>
      <p className="reveal font-italic-serif text-[20px] md:text-[24px] text-[var(--cg-cream)]/70 mb-12 leading-relaxed">
        Chat with Isabelle directly via WhatsApp to arrange your next steps.
      </p>

      <div className="reveal flex flex-wrap justify-center gap-4 mb-16">
        <a
          data-testid="contact-cta-whatsapp"
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold"
        >
          <MessageCircle size={15} />
          Request Instant Advice
        </a>
        <a
          data-testid="contact-cta-phone"
          href={PHONE_HREF}
          className="btn-ghost"
        >
          <Phone size={14} />
          {PHONE_DISPLAY}
        </a>
      </div>

      <div className="reveal">
        <DiamondDivider />
      </div>

      <p className="reveal mt-12 text-[13px] tracking-[0.18em] uppercase text-[var(--cg-cream)]/50">
        Discreet · Personal · Trusted
      </p>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────
// Footer
// ──────────────────────────────────────────────────────────────────
const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative pt-20 pb-10 px-6 md:px-12 bg-black"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="grid md:grid-cols-12 gap-12 mb-16">
        {/* Brand */}
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-[var(--cg-gold)] text-xl">◆</span>
            <span className="font-display text-[22px] tracking-[0.18em] text-[var(--cg-cream)]">
              COLLECTIVE<span className="text-gold-grad">GOLD</span>
            </span>
          </div>
          <p className="font-italic-serif text-[18px] text-[var(--cg-cream)]/70 mb-6 leading-relaxed max-w-md">
            Luxury Jewellery, Watches &amp; Antiques{" "}
            <span className="text-[var(--cg-gold)]">Sourcing &amp; Sales</span>{" "}
            Consultancy.
          </p>
          <a
            data-testid="footer-cta-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold !text-[10px] !py-3 !px-6"
          >
            <MessageCircle size={13} />
            Request Instant Advice
          </a>
        </div>

        {/* Specialising in */}
        <div className="md:col-span-3">
          <div className="eyebrow mb-5">Specialising In</div>
          <ul className="space-y-3 text-[14px] text-[var(--cg-cream)]/75">
            <li>Direct Purchase Offers</li>
            <li>Private Sourcing &amp; Acquisitions</li>
            <li>Auction &amp; Insurance Valuations</li>
            <li>Sorting &amp; Selling Collections</li>
            <li>Jewellery Cleaning &amp; Restoration</li>
          </ul>
        </div>

        {/* Connect */}
        <div className="md:col-span-4">
          <div className="eyebrow mb-5">Connect</div>
          <ul className="space-y-3 text-[14px] text-[var(--cg-cream)]/75">
            <li>
              <a
                data-testid="footer-link-whatsapp"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--cg-gold)] transition-colors flex items-center gap-2 link-underline"
              >
                <MessageCircle size={13} /> WhatsApp
              </a>
            </li>
            <li>
              <a
                data-testid="footer-link-phone"
                href={PHONE_HREF}
                className="hover:text-[var(--cg-gold)] transition-colors flex items-center gap-2 link-underline"
              >
                <Phone size={13} /> {PHONE_DISPLAY}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="pt-8 border-t border-[rgba(241,200,127,0.12)] flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-[12px] text-[var(--cg-cream)]/45 tracking-wide">
          © {new Date().getFullYear()} CollectiveGold. All rights reserved.
        </p>
        <p className="text-[11px] tracking-[0.28em] uppercase text-[var(--cg-cream)]/40">
          Discreet · Personal · Trusted
        </p>
      </div>
    </div>
  </footer>
);

// ──────────────────────────────────────────────────────────────────
// Floating WhatsApp button (mobile-friendly persistent CTA)
// ──────────────────────────────────────────────────────────────────
const FloatingWhatsApp = () => (
  <a
    data-testid="floating-whatsapp"
    href={WHATSAPP_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-[0_12px_36px_-8px_rgba(241,200,127,0.6)] hover:scale-110 transition-transform duration-300"
    style={{
      background:
        "linear-gradient(180deg, #f7dba3 0%, #f1c87f 55%, #c89f5d 100%)",
    }}
    aria-label="Chat on WhatsApp"
  >
    <MessageCircle size={22} strokeWidth={1.6} color="#0a0a0a" />
  </a>
);

// ──────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────
function App() {
  useReveal();

  return (
    <div className="App relative" data-testid="app-root">
      <Navigation />
      <main>
        <Hero />
        <WhatsItWorth />
        <Sourcing />
        <HowItWorks />
        <ValuationTypes />
        <About />
        <Markets />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
